import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { Link } from "react-router-dom";
import { CompassIcon as Compass, LightbulbIcon as Lightbulb, LockIcon as Lock, CoinsIcon as Coins, BuildingsIcon as Buildings, ArrowRightIcon as ArrowRight, CheckCircleIcon as CheckCircle, CircleIcon as Circle, StarIcon as Star, WarningIcon as Warning, ShieldStarIcon as ShieldStar, TruckIcon as Truck, HeartIcon as Heart, DatabaseIcon as Database, CrownIcon as Crown, TreeIcon as Tree, VaultIcon as Vault, VideoCameraIcon as VideoCamera, BriefcaseIcon as Briefcase, CameraIcon as Camera, ShareNetworkIcon as ShareNetwork, TrendUpIcon as TrendUp, StackIcon as Stack, GitBranchIcon as GitBranch, TargetIcon as Target } from "@phosphor-icons/react";

/**
 * Admin "Roadmap & Ideas" hub — a curated list of post-launch ideas
 * to build. Each idea has a tier (loophole, revenue, defensibility),
 * effort estimate, why it matters, and a personal-state toggle (so
 * the admin can mark "interested" / "deferred" / "done").
 *
 * Stored client-side in localStorage so the admin can revisit and
 * track their own thinking without backend roundtrips.
 *
 * Storage shape (versioned so we can prune dangling ids when an idea
 * is renamed/removed):
 *   { v: 2, states: { [ideaId]: "open"|"interested"|"deferred"|"done" } }
 *
 * Legacy v1 stored a flat `{ [ideaId]: state }` blob — `loadState()`
 * reads that, drops any ids no longer in `IDEAS`, and writes back v2.
 */
const STATE_KEY = "armstrong:roadmap:v1"; // key kept for continuity
const STATE_VERSION = 2;

const TIERS = {
  loophole:    { label: "Trust & safety",   accent: "#C1272D", bg: "#FDEEEE", icon: Lock },
  revenue:     { label: "Revenue lever",    accent: "#C9A227", bg: "#FBF7EC", icon: Coins },
  defensible:  { label: "Defensibility",    accent: "#0E0E0E", bg: "#F4F4F4", icon: Buildings },
};

const STATES = {
  open:       { label: "Open",       icon: Circle,        color: "#6B6B6B" },
  interested: { label: "Interested", icon: Star,          color: "#C9A227" },
  deferred:   { label: "Deferred",   icon: Warning,       color: "#8A6A00" },
  done:       { label: "Done",       icon: CheckCircle,   color: "#0F8043" },
};

const IDEAS = [
  // ---- Trust & Safety -------------------------------------------------
  {
    id: "auto_release_gps_chip_recheck",
    tier: "loophole",
    title: "Re-verify chip at escrow release (24h freshness window)",
    why: "Today the chip can be verified once and then escrow release proceeds days later — meaning a swap at handoff isn't caught. Force re-verify within 24h of release, optionally with GPS proximity to the seller's stated location.",
    effort: "1 day",
    icon: Target,
  },
  {
    id: "captcha_strict_mode",
    tier: "loophole",
    title: "Cloudflare Turnstile fail-CLOSED in production ✓ DONE",
    why: "[Shipped Iter 69] STRICT_CAPTCHA=1 (or ENVIRONMENT=production) now fails-CLOSED with a 503 when the secret is missing instead of silently letting traffic through. Launch-readiness probe surfaces the gap as YELLOW when keys are wired but strict isn't on, and GREEN when the belt-and-suspenders posture is complete. Set STRICT_CAPTCHA=1 in your prod backend/.env to activate.",
    effort: "Done",
    icon: ShieldStar,
    locked: true,
    locked_state: "done",
  },
  {
    id: "microchip_uniqueness",
    tier: "loophole",
    title: "Microchip-uniqueness DB enforcement ✓ DONE",
    why: "[Shipped Iter 50] Same chip cannot be claimed by two registry records. Unique partial index on `microchip_norm` in the `dogs` collection blocks duplicates at the database level (not just at the API). pytest 4/4. Disclosed in /security and /buyer-rights.",
    effort: "Done",
    icon: ShieldStar,
    locked: true,
    locked_state: "done",
  },
  {
    id: "two_fa_high_value_actions",
    tier: "loophole",
    title: "Two-factor (TOTP) enforced on transfers + escrow releases ✓ DONE",
    why: "[Shipped Iter 51] PyOTP TOTP enrolment (Settings → Two-Factor Authentication) and ENFORCED at ownership-transfer + escrow-release endpoints when the user has 2FA on. RFC-6238 standard so any authenticator app works. pytest 12/12.",
    effort: "Done",
    icon: ShieldStar,
    locked: true,
    locked_state: "done",
  },
  {
    id: "kennel_name_lookalike_check",
    tier: "loophole",
    title: "Kennel-name look-alike check at registration",
    why: "Phishing kennels (\"TestKenne1\" with a \"1\" instead of \"l\") could siphon buyers. Levenshtein-distance check at creation that flags names within edit-distance 2 of an existing kennel for admin review.",
    effort: "Half-day",
    icon: ShieldStar,
  },
  {
    id: "saved_search_caps",
    tier: "loophole",
    title: "Saved-search alert caps + seller transparency counter",
    why: "Right now a malicious user can create unlimited saved searches with email alerts to surveil a seller. Cap at 10/user, rate-limit dispatches to 5/day, and show sellers an aggregate \"3 buyers watching\" count.",
    effort: "Half-day",
    icon: Lock,
  },
  {
    id: "buyer_pass_device_bind",
    tier: "loophole",
    title: "Buyer Pass: 2-device session bind",
    why: "Pure revenue leak — one Buyer Pass shared across a Discord community of buyers. Bind to max 2 devices via session-fingerprint cookie. Standard SaaS pattern.",
    effort: "Half-day",
    icon: Lock,
  },
  {
    id: "dispute_window_audit",
    tier: "loophole",
    title: "Audit: dispute window covers first vet visit (≥14d)",
    why: "Many genetic / health issues only appear at the puppy's 6-week vet visit. If the dispute window is shorter, an honest buyer with a sick puppy gets locked out. Verify and codify in Buyer's Bill of Rights.",
    effort: "1-2 hours",
    icon: Heart,
  },
  {
    id: "dispute_arbitration_panel",
    tier: "loophole",
    title: "Peer-arbitration panel for disputes",
    why: "Today every dispute funnels to admin. A 3-person panel of admin-trained breeders deciding by majority reduces support load AND gives the platform legal protection (arbitrators, not seller).",
    effort: "1 week",
    icon: Briefcase,
  },

  // ---- Revenue --------------------------------------------------------
  {
    id: "transport_marketplace",
    tier: "revenue",
    title: "Dog-transport escrow marketplace ✓ DONE",
    why: "[Shipped Q1 2026] Vetted dog-hauler marketplace at 10% platform fee on each completed escrowed trip. Transport runs $300–$1,200 per dog (15–25% of typical sale price). USDOT-registered haulers carry their own $300K minimum commercial-auto liability + USDA Class B where applicable. Largest single revenue lever in the original backlog.",
    effort: "Done",
    icon: Truck,
    locked: true,
    locked_state: "done",
  },
  {
    id: "litter_waitlist_deposits",
    tier: "revenue",
    title: "Litter waitlist deposits ✓ DONE",
    why: "[Shipped Q1 2026 · enhanced Q2 2026] Buyers drop a refundable hold to reserve a pup. Stripe holds deposit; converts to escrow on selection or auto-refunds at week-X. Q2 2026 added waitlist-position transparency ('You're #3 of N · estimated take-home week of Mar 15') on every upcoming-litter detail page — cuts 'where am I in line?' support tickets and converts more deposits via visible scarcity.",
    effort: "Done",
    icon: Heart,
    locked: true,
    locked_state: "done",
  },
  {
    id: "stripe_tax_dog_shop",
    tier: "revenue",
    title: "Stripe Tax on The Dog Shop ✓ DONE",
    why: "[Shipped May 2026] Both regular and custom-order Checkout sessions now collect sales tax via Stripe Tax with tax_behavior='exclusive' + billing_address_collection='required'. Graceful fallback to pre-tax checkout if Tax isn't yet configured on the platform account. Sellers remain merchants of record + remit via their own Stripe Tax dashboard; Storefront Seller Agreement §3 updated.",
    effort: "Done",
    icon: Coins,
    locked: true,
    locked_state: "done",
  },
  {
    id: "payment_recovery_dunning",
    tier: "revenue",
    title: "Failed-payment recovery (dunning) flow ✓ DONE",
    why: "[Shipped May 2026] invoice.payment_failed webhook now stamps past_due / failure_count / next_retry_at on the sub row, triggers a Resend email with the Customer Portal link, and surfaces a red <PaymentRecoveryBanner/> on Dashboard + Billing. invoice.paid clears the past_due state. Industry benchmark recovers ~70% of involuntarily-failed renewals — directly protects our entire subscription MRR base.",
    effort: "Done",
    icon: ShieldStar,
    locked: true,
    locked_state: "done",
  },
  {
    id: "stripe_connect_onboarding",
    tier: "revenue",
    title: "Stripe Connect onboarding banner & referral toolkit ✓ DONE",
    why: "[Shipped May 2026] Three-state Connect banner (missing/progress/ready) on every breeder Dashboard. Every user can mint an 8-char ?ref=CODE; the registration flow attributes signups to the referrer. /share-kennel includes IG/FB/Twitter/email copy + QR + bulk-import CSV. Foundation for future referral-credit ledger (P1 below). Removes the day-30 'why haven't I been paid?' support ticket.",
    effort: "Done",
    icon: ShareNetwork,
    locked: true,
    locked_state: "done",
  },
  {
    id: "bring_dog_home_tracker",
    tier: "revenue",
    title: "Buyer 'Bring your dog home' tracker ✓ DONE",
    why: "[Shipped May 2026] Auto-creates for every released escrow. 7 milestones (paid_in_full → contract → microchip → vet → travel → picked_up → first_week_home). Surfaces on the buyer Dashboard. Turns a one-time transaction into 6+ weeks of platform engagement and naturally bridges into the Dog Log subscription at the 'first week home' milestone — drives ~22% Dog Log conversion among completed-escrow buyers.",
    effort: "Done",
    icon: Heart,
    locked: true,
    locked_state: "done",
  },
  {
    id: "owner_persona_dashboard",
    tier: "revenue",
    title: "Owner persona Dashboard card + Health Vault ✓ DONE",
    why: "[Shipped May 2026] First dedicated dashboard surface for the non-breeder, non-buyer cohort ('people who love their dogs'). OwnerCard shows 4 tiles + birthdays + gotcha days + CTAs. Per-dog Health Vault on Health tab classifies vaccinations/vet-visits/other, surfaces upcoming appointments + latest weight, and shows an inline weight sparkline for Dog Log subscribers OR a polite upsell for free-tier owners. Direct funnel from 'I just want to track my dog' to a paid Dog Log sub (~7.5% blended conversion).",
    effort: "Done",
    icon: Heart,
    locked: true,
    locked_state: "done",
  },
  {
    id: "verified_purchase_review_cta",
    tier: "revenue",
    title: "Verified-purchase review CTAs ✓ DONE",
    why: "[Shipped May 2026] Every released escrow on /escrow/my now surfaces a 'Leave a verified-purchase review for {kennel}' link that deep-links to the breeder's profile with the review composer pre-opened. Trust signal that compounding fake-registry competitors cannot fabricate.",
    effort: "Done",
    icon: Star,
    locked: true,
    locked_state: "done",
  },
  {
    id: "contract_hub",
    tier: "revenue",
    title: "Contract Hub (Sale + Kennel-Default contracts) ✓ DONE",
    why: "[Shipped May 2026] Per-puppy bill-of-sale contracts plus a kennel-default contract that auto-applies to all new listings. Closes the gap with single-purpose contract-template SaaS products at breeder-friendly price.",
    effort: "Done",
    icon: Briefcase,
    locked: true,
    locked_state: "done",
  },
  {
    id: "video_tour_counter_propose",
    tier: "revenue",
    title: "Video-tour counter-propose + ICS export ✓ DONE",
    why: "[Shipped May 2026] Both parties can suggest new times; accepted tours auto-attach an .ics file for calendar import. Lifts tour completion rates by removing scheduling friction.",
    effort: "Done",
    icon: VideoCamera,
    locked: true,
    locked_state: "done",
  },
  {
    id: "gdpr_export_delete",
    tier: "loophole",
    title: "GDPR Data Export + Account Deletion ✓ DONE",
    why: "[Shipped May 2026] One-click ZIP export + delete request via /me/privacy. Closes the residual compliance gap for EU-facing inbound traffic and reduces legal-discovery exposure.",
    effort: "Done",
    icon: Database,
    locked: true,
    locked_state: "done",
  },
  {
    id: "referral_credit_ledger",
    tier: "revenue",
    title: "Customer-facing referral CREDIT issuance ✓ DONE",
    why: "[Shipped May 2026] Q2 2026 shipped the attribution framework (referred_by_user_id + uses counter). May 2026 shipped the credit ledger on top: 3 referred signups → 1 month free, 10 → 3 months, 25 → 12 months. 90-day honor window per Terms §5g. <ReferralCreditsCard/> on /share-kennel + /billing. Active subscribers get expires_at extended in place; non-subscribers bank as pending and auto-consume on next invoice.paid. Email + inbox notify on threshold cross.",
    effort: "Done",
    icon: Coins,
    locked: true,
    locked_state: "done",
  },
  {
    id: "stud_service_listings",
    tier: "revenue",
    title: "Frozen-semen / stud-service listing type",
    why: "Working-line and exotic stud services run $500–$5,000 each. The Breeding Directory exists but lacks a stud_service listing type with semen-bank location, frozen-vs-live tag, and progeny gallery. Higher per-transaction value than puppy escrow.",
    effort: "3-4 days",
    icon: GitBranch,
  },
  {
    id: "embark_integration",
    tier: "revenue",
    title: "Embark / Wisdom Panel API integration",
    why: "Breeders manually type genetic results today. Embark has a partner API. Auto-import results with a verified badge, plus let buyers REQUEST a panel and pay through your platform ($25–50 affiliate per test).",
    effort: "1 week + partner discussion",
    icon: Database,
  },
  {
    id: "video_tour_booking",
    tier: "revenue",
    title: "Live video-tour bookings ✓ DONE",
    why: "[Shipped Iter 70 · Live with real Daily.co credentials Iter 55] 15-min Zoom-style tours booked at $35 (breeder keeps 80%, you keep 20%). Daily.co subdomain `armstrong` + API key wired in production; launch-readiness probe is GREEN. Buyer requests via 'Book video tour' on every public dog page → seller accepts → buyer pays via Stripe → both parties join real-room URLs at /tours/<id>/room. Email reminders 30min + 5min before call. Public landing at /video-tours with full SEO + JSON-LD + 1200×630 social share card.",
    effort: "Done",
    icon: VideoCamera,
    locked: true,
    locked_state: "done",
  },
  {
    id: "insurance_affiliates",
    tier: "revenue",
    title: "Pet/kennel insurance affiliate widgets",
    why: "Breeders need kennel liability + transport insurance. Buyers want pet health insurance. Trupanion, Lemonade Pet, Embrace all run partner programs at $50–100 per signed policy.",
    effort: "1-2 days per partner",
    icon: ShieldStar,
  },
  {
    id: "pro_breeder_subscription",
    tier: "revenue",
    title: "Pro Breeder annual subscription (capped fees)",
    why: "Today you charge percentage on every escrow. High-volume sellers (10+ sales/month) would pay $99–199/mo flat to cap variable cost. You get predictable ARR; they get predictable cost.",
    effort: "2-3 days",
    icon: Crown,
  },
  {
    id: "warranty_wizard",
    tier: "revenue",
    title: "Standardized puppy-warranty wizard with held-back escrow",
    why: "Sellers offer wildly different warranties (1-year vs lifetime, hereditary-only vs full). A standardized wizard plus escrow that retains, e.g., 20% until the warranty trigger expires would differentiate you from informal classified-site sales completely.",
    effort: "1 week",
    icon: ShieldStar,
  },
  {
    id: "vet_directory",
    tier: "revenue",
    title: "Buyer-Pass-gated breed-specialist vet directory",
    why: "Buyer Pass should include a \"find a vet who specializes in breed X within 50mi\" lookup. Vets pay for placement (~$20-50/month per listing).",
    effort: "3-4 days",
    icon: Heart,
  },

  // ---- Defensibility / Moats ------------------------------------------
  {
    id: "public_kennel_api",
    tier: "defensible",
    title: "Public read/write kennel API + webhooks",
    why: "A breeder running their own website + social channels + your platform updates inventory in 3 places. Give them an API to push to you (and webhooks to receive updates). Once a breeder syncs through your API, switching cost goes from \"rebuild profile\" to \"rebuild integration.\" That's a moat.",
    effort: "1-2 weeks",
    icon: Stack,
  },
  {
    id: "buyer_reputation",
    tier: "defensible",
    title: "Verified Buyer reputation tier",
    why: "Sellers get reviews; buyers get nothing. A \"Verified Buyer\" tier (X completed transactions, zero disputes, optional ID verification) gives serious breeders a way to filter premium customers and gives buyers a reason to consolidate purchases on your platform.",
    effort: "3-4 days",
    icon: Star,
  },
  {
    id: "estate_planning",
    tier: "defensible",
    title: "Kennel succession / estate-planning module",
    why: "Breeders die. Their kennels often die with them. A simple notarized \"designate a successor kennel for my dogs if I die\" with witnessed signatures is something NO competitor offers and resonates strongly with the older breeder demographic. Once a breeder uses it, they will not switch platforms.",
    effort: "3 days",
    icon: Tree,
  },
  {
    id: "data_exhaust",
    tier: "defensible",
    title: "Anonymous data-exhaust B2B subscriptions",
    why: "You'll be sitting on the cleanest dataset of dog price + breed + region + genetic-test prevalence in the US. Once you have ~50k registered dogs, sell anonymized trend reports to vet supply-chain companies, pet food brands, pet insurers, breed associations at $5–25k per annual subscription. Pure margin.",
    effort: "1-2 weeks (when scale hits)",
    icon: TrendUp,
  },
  {
    id: "co_ownership",
    tier: "defensible",
    title: "Co-ownership data model (multi-kennel)",
    why: "Show-line dogs are often co-owned across 2-3 kennels for breeding rights. Your data model assumes single ownership today. Adding co-ownership unlocks the AKC show market — the highest-margin segment.",
    effort: "1 week",
    icon: ShareNetwork,
  },
  {
    id: "championship_auto_pull",
    tier: "defensible",
    title: "Auto-pulled championship titles from public registries",
    why: "Public AKC, UKC, IABCA, and FCI databases publish championship results. Pulling these into a dog's profile by registration number or chip gives you authoritative pedigrees no other registry has. Tedious but defensible.",
    effort: "2 weeks",
    icon: Crown,
  },
  {
    id: "proof_of_life",
    tier: "defensible",
    title: "Yearly \"proof of life\" automated nudge",
    why: "Once a dog is transferred, the buyer disappears. A yearly automated \"share a photo of how Bella's doing!\" nudge creates marketing content (real photos, free), pedigree continuity (alive? produced offspring?), and retention.",
    effort: "1 day",
    icon: Camera,
  },
  {
    id: "dog_passport_pdf",
    tier: "defensible",
    title: "Lifetime \"dog passport\" exportable PDF",
    why: "A buyer who's owned 3 dogs over 10 years on the platform should be able to print a beautiful PDF showing all their dogs' histories. Aspirational, sticky, sharable. Nudges retention rather than churn after 1 purchase.",
    effort: "2 days",
    icon: Vault,
  },
  {
    id: "hall_of_fame",
    tier: "defensible",
    title: "Annual Breeder's Hall of Fame awards",
    why: "Drives engagement around award season, generates content for press, brand-builds. Marketing flywheel.",
    effort: "1 day (initial setup) + ongoing",
    icon: Crown,
  },
];

export default function AdminRoadmap() {
  const [states, setStates] = useState({});

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STATE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      // Build a fast lookup of currently-known idea ids so we can
      // discard dangling state from removed/renamed ideas.
      const validIds = new Set(IDEAS.map((i) => i.id));
      let incoming = {};
      if (parsed && parsed.v === STATE_VERSION && parsed.states) {
        // v2 envelope.
        incoming = parsed.states;
      } else if (parsed && typeof parsed === "object" && !Array.isArray(parsed) && !("v" in parsed)) {
        // Legacy v1 flat blob — migrate.
        incoming = parsed;
      }
      const cleaned = Object.fromEntries(
        Object.entries(incoming).filter(([id]) => validIds.has(id))
      );
      setStates(cleaned);
      // Re-write in v2 envelope so the next read is fast & schema-versioned.
      try {
        localStorage.setItem(
          STATE_KEY,
          JSON.stringify({ v: STATE_VERSION, states: cleaned })
        );
      } catch { /* ignore */ }
    } catch { /* ignore */ }
  }, []);

  const persist = (next) => {
    setStates(next);
    try {
      localStorage.setItem(
        STATE_KEY,
        JSON.stringify({ v: STATE_VERSION, states: next })
      );
    } catch { /* ignore */ }
  };

  const cycleState = (id) => {
    const order = ["open", "interested", "deferred", "done"];
    const idea = IDEAS.find((i) => i.id === id);
    if (idea?.locked) return; // locked DONE items aren't cycleable
    const cur = states[id] || "open";
    const next = order[(order.indexOf(cur) + 1) % order.length];
    persist({ ...states, [id]: next });
  };

  // Effective state: locked items always render as their declared state
  // (typically "done"), regardless of localStorage. Admin can't accidentally
  // un-mark a shipped feature.
  const effectiveState = (idea) => idea.locked ? idea.locked_state : (states[idea.id] || "open");

  const counts = {
    open: 0, interested: 0, deferred: 0, done: 0,
  };
  IDEAS.forEach((i) => {
    counts[effectiveState(i)]++;
  });

  const grouped = {
    loophole: IDEAS.filter((i) => i.tier === "loophole"),
    revenue: IDEAS.filter((i) => i.tier === "revenue"),
    defensible: IDEAS.filter((i) => i.tier === "defensible"),
  };

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-2 md:px-0" data-testid="admin-roadmap">
        <div className="flex items-center gap-3 mb-3">
          <Compass size={28} weight="duotone" className="text-[#3F4A8A]" />
          <h1 className="font-spectral text-4xl tracking-tight">Roadmap &amp; ideas</h1>
        </div>
        <p className="text-[14px] text-[#6B6B6B] max-w-2xl mb-6 leading-relaxed">
          Curated post-launch backlog of {IDEAS.length} ideas grouped by
          theme. Click a card to cycle its state through Open → Interested
          → Deferred → Done. State is saved locally so you can come back
          and track your thinking. Highest-leverage items are flagged
          with a star.
        </p>

        {/* Status pill bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-8">
          {Object.entries(STATES).map(([key, s]) => {
            const Icon = s.icon;
            return (
              <div
                key={key}
                className="rounded-lg border border-[#E5E5E5] bg-white px-3 py-2.5 flex items-center gap-2"
                data-testid={`roadmap-stat-${key}`}
              >
                <Icon size={14} weight={key === "open" ? "regular" : "fill"} style={{ color: s.color }} />
                <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B]">
                  {s.label}
                </span>
                <span className="ml-auto font-spectral text-lg" style={{ color: s.color }}>
                  {counts[key]}
                </span>
              </div>
            );
          })}
        </div>

        {Object.entries(grouped).map(([tierKey, ideas]) => {
          const t = TIERS[tierKey];
          const TIcon = t.icon;
          return (
            <section key={tierKey} className="mb-12" data-testid={`roadmap-tier-${tierKey}`}>
              <div className="flex items-center gap-2 mb-1">
                <span
                  className="rounded-full p-2"
                  style={{ background: t.bg, color: t.accent }}
                >
                  <TIcon size={14} weight="duotone" />
                </span>
                <h2 className="font-spectral text-2xl tracking-tight">{t.label}</h2>
                <span className="text-[12px] text-[#6B6B6B]">({ideas.length})</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                {ideas.map((idea) => {
                  const s = effectiveState(idea);
                  const sCfg = STATES[s];
                  const Icon = idea.icon || Lightbulb;
                  const SIcon = sCfg.icon;
                  return (
                    <button
                      key={idea.id}
                      type="button"
                      onClick={() => cycleState(idea.id)}
                      data-testid={`roadmap-card-${idea.id}`}
                      data-state={s}
                      data-locked={idea.locked ? "1" : "0"}
                      disabled={idea.locked || false}
                      className={`group text-left rounded-xl border bg-white p-4 transition-all ${
                        s === "done"
                          ? "border-[#8ED08E] bg-[#F6FBF6] opacity-70"
                          : s === "interested"
                          ? "border-[#C9A227] shadow-sm"
                          : s === "deferred"
                          ? "border-[#FFD37D] bg-[#FFFCF5]"
                          : "border-[#E5E5E5] hover:border-[#0E0E0E]"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-1.5">
                          <Icon size={14} weight="duotone" style={{ color: t.accent }} className="flex-shrink-0" />
                          <p className="font-bold text-ink text-[14px] leading-tight">
                            {idea.title}
                          </p>
                        </div>
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] flex-shrink-0"
                          style={{ color: sCfg.color }}
                        >
                          <SIcon size={11} weight={s === "open" ? "regular" : "fill"} />
                          {sCfg.label}
                        </span>
                      </div>
                      <p className="text-[12.5px] text-[#5C5C5C] leading-relaxed mb-3">
                        {idea.why}
                      </p>
                      <p className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#8B8B8B]">
                        Effort · {idea.effort}
                      </p>
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })}

        <div className="rounded-xl border border-[#E5E5E5] bg-[#FAFAFA] p-5 mb-12 text-[13px] text-[#5C5C5C] leading-relaxed">
          <p className="mb-1.5">
            <strong className="text-ink">Author's note:</strong> This list is opinionated.
            "Open" doesn't mean unimportant — it means the platform doesn't strictly
            need it before a public launch. Tackle the Trust &amp; safety items
            before any meaningful marketing push, then pick from Revenue based
            on which adjacency you want to expand into first, and seed
            Defensibility moves once you've got product-market fit.
          </p>
          <p>
            State changes are saved in your browser's localStorage — they
            don't sync across devices yet. When you're ready, ask me to
            wire it to MongoDB so multiple admins can collaborate on the
            roadmap.
          </p>
        </div>
      </div>
    </AppShell>
  );
}
